import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  VStack,
  Flex,
  Divider,
  Link,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  IconButton,
  Select,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { useLanguage } from "../contexts/LanguageContext";
import BusinessCard from "../components/BusinessCard";
import Navbar from "../components/Navbar";
import PurchaseForm from "../components/PurchaseForm";
import { FaShoppingCart, FaRegClock, FaSearch, FaTimes } from "react-icons/fa";
import { Spinner } from "@chakra-ui/react";
import { apiUrl } from "../config";

const PurchasePage = () => {
  const { campaignId, agentId } = useParams();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { language, translate } = useLanguage();
  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const totalCouponValue = businesses.reduce(
    (sum, b) => sum + (Number(b.maxValue) || 0),
    0
  );

  const getLocalizedText = (field) => {
    if (typeof field === "object" && field !== null) {
      return field[language] || field.en || "";
    }
    return field || "";
  };

  // Use the English value (or the raw string) as a stable category key so a
  // language switch doesn't invalidate the user's selection; the label
  // shown in the Select is still localized.
  const getCategoryKey = (cat) => {
    if (typeof cat === "object" && cat !== null) return cat.en || "";
    return cat || "";
  };

  const categories = useMemo(() => {
    const localize = (field) => {
      if (typeof field === "object" && field !== null) {
        return field[language] || field.en || "";
      }
      return field || "";
    };
    const map = new Map();
    businesses.forEach((b) => {
      const key = getCategoryKey(b.category);
      if (!key) return;
      if (!map.has(key)) map.set(key, localize(b.category));
    });
    return Array.from(map.entries())
      .sort((a, b) => a[1].localeCompare(b[1]))
      .map(([key, label]) => ({ key, label }));
  }, [businesses, language]);

  const filteredBusinesses = useMemo(() => {
    const localize = (field) => {
      if (typeof field === "object" && field !== null) {
        return field[language] || field.en || "";
      }
      return field || "";
    };
    const term = searchTerm.trim().toLowerCase();
    return businesses.filter((b) => {
      if (selectedCategory !== "all") {
        if (getCategoryKey(b.category) !== selectedCategory) return false;
      }
      if (term) {
        const haystack = [
          localize(b.name),
          localize(b.category),
          localize(b.offerTitle),
          localize(b.offerDescription),
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [businesses, searchTerm, selectedCategory, language]);

  useEffect(() => {
    const fetchCampaignAndBusinesses = async () => {
      try {
        const campaignRef = doc(db, "campaigns", campaignId);
        const campaignSnap = await getDoc(campaignRef);
        if (campaignSnap.exists()) {
          setCampaign(campaignSnap.data());
        } else {
          setError("Campaign not found.");
          return;
        }

        const businessesRef = collection(db, "businesses");
        const q = query(
          businessesRef,
          where("campaignId", "==", campaignId),
          where("approved", "==", true)
        );
        const querySnapshot = await getDocs(q);
        // logoUrl is the public storage.googleapis.com URL written by the
        // backend on upload — it's already on the business doc, so just use
        // it. (Earlier code re-fetched via the Firebase Storage SDK, which
        // requires a download token that Admin-SDK uploads don't have, and
        // ended up overwriting the real URL with null.)
        const businessList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setBusinesses(businessList);
      } catch (err) {
        console.error("Error fetching data: ", err);
        setError("An error occurred while fetching data.");
      } finally {
        setLoading(false);
      }
    };

    fetchCampaignAndBusinesses();
  }, [campaignId]);

  // Fire a view ping once per browser session per campaign/agent. The
  // sessionStorage gate keeps refreshes and back-button revisits from
  // inflating the counter. Errors are swallowed — a tracking miss should
  // never break the buyer-facing page.
  useEffect(() => {
    if (!campaignId || !agentId) return;
    const storageKey = `viewLogged:${campaignId}:${agentId}`;
    try {
      if (sessionStorage.getItem(storageKey)) return;
    } catch (_) {
      // Safari private mode etc. — fall through and just send the ping.
    }
    fetch(`${apiUrl}/campaign/${campaignId}/agent/${agentId}/view`, {
      method: "POST",
      keepalive: true,
    })
      .then(() => {
        try {
          sessionStorage.setItem(storageKey, "1");
        } catch (_) {}
      })
      .catch(() => {});
  }, [campaignId, agentId]);

  if (loading) {
    return (
      <Box
        minH="100vh"
        bg="primaryBackground"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="xl" color="secondary" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        minH="100vh"
        bg="primaryBackground"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      display="flex"
      flexDirection="column"
      bg="primaryBackground"
    >
      <Navbar external={true} />

      <Container
        maxW="1200px"
        px={{ base: 4, md: 6, lg: 8 }}
        py={{ base: 8, md: 12 }}
      >
        {/* Header Section */}
        {(() => {
          const heroImage = campaign.heroImage;
          const hasHero = !!heroImage?.url;
          const headingColor = hasHero ? "white" : undefined;
          const bodyColor = hasHero ? "whiteAlpha.900" : "gray.600";
          return (
            <Box
              position="relative"
              width="100%"
              mb={hasHero ? 12 : 0}
              textAlign={{ base: "center", md: "left" }}
              borderRadius={hasHero ? "xl" : undefined}
              overflow={hasHero ? "hidden" : undefined}
              backgroundImage={hasHero ? `url(${heroImage.url})` : undefined}
              backgroundSize="cover"
              backgroundPosition="center"
              px={hasHero ? { base: 6, md: 10 } : 0}
              py={hasHero ? { base: 10, md: 16 } : 0}
              minH={hasHero ? { base: "320px", md: "380px" } : undefined}
            >
              {hasHero && (
                <Box
                  position="absolute"
                  inset={0}
                  bgGradient="linear(to right, rgba(0,0,0,0.65), rgba(0,0,0,0.25))"
                  pointerEvents="none"
                />
              )}
              <Box position="relative">
                <Heading as="h1" size="xl" mb={4} color={headingColor}>
                  {getLocalizedText(campaign.name)}
                </Heading>

                <Text fontSize="lg" color={bodyColor} mb={8}>
                  {getLocalizedText(campaign.description)}
                </Text>

                {/* Purchase Info Section */}
                <Flex
                  direction="column"
                  align={{ base: "center", md: "start" }}
                  justify={{ base: "center", md: "start" }}
                  textAlign={{ base: "center", md: "left" }}
                  mb={hasHero ? 0 : 8}
                >
                  <Flex
                    align="baseline"
                    gap={2}
                    wrap="wrap"
                    justify={{ base: "center", md: "start" }}
                    mb={4}
                  >
                    <Text fontSize="lg" color={bodyColor}>
                      Only
                    </Text>
                    <Text
                      fontSize="4xl"
                      fontWeight="bold"
                      color={headingColor}
                    >
                      ${campaign.couponBookPrice}
                    </Text>
                    {totalCouponValue > 0 && (
                      <Text fontSize="md" color={bodyColor}>
                        Potential value up to $
                        {Math.round(totalCouponValue).toLocaleString("en-US")}
                      </Text>
                    )}
                  </Flex>

                  {(() => {
                    const secondary = campaign.secondaryButton;
                    const secondaryUrl =
                      secondary?.url || campaign.offeringURL || "";
                    const secondaryLabel =
                      secondary?.label?.[language] ||
                      secondary?.label?.en ||
                      "Learn More";
                    return (
                      <Flex
                        gap={3}
                        wrap="wrap"
                        justify={{ base: "center", md: "start" }}
                      >
                        <Button
                          onClick={onOpen}
                          size="md"
                          variant="secondary"
                          leftIcon={<FaShoppingCart />}
                          minW="200px"
                        >
                          {translate("businessList.goToPurchase")}
                        </Button>
                        {secondaryUrl && (
                          <Button
                            as="a"
                            href={secondaryUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="md"
                            variant="outline"
                            minW="200px"
                            {...(hasHero && {
                              color: "white",
                              borderColor: "whiteAlpha.800",
                              _hover: {
                                bg: "whiteAlpha.200",
                                borderColor: "white",
                                color: "white",
                              },
                            })}
                          >
                            {secondaryLabel}
                          </Button>
                        )}
                      </Flex>
                    );
                  })()}

                  {/* Campaign Dates */}
                  {campaign?.redemptionStartDate &&
                    campaign?.redemptionEndDate && (
                      <Flex align="center" gap={2} mt={4} color={bodyColor}>
                        <FaRegClock />
                        <Text fontSize="sm" color={bodyColor}>
                          {translate("businessList.validFrom")}{" "}
                          <Text as="span" fontWeight="semibold">
                            {new Date(
                              campaign.redemptionStartDate?.toDate?.() ||
                                campaign.redemptionStartDate
                            ).toLocaleDateString(
                              language === "fr" ? "fr-CA" : "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              }
                            )}
                          </Text>{" "}
                          {translate("businessList.to")}{" "}
                          <Text as="span" fontWeight="semibold">
                            {new Date(
                              campaign.redemptionEndDate?.toDate?.() ||
                                campaign.redemptionEndDate
                            ).toLocaleDateString(
                              language === "fr" ? "fr-CA" : "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              }
                            )}
                          </Text>
                        </Text>
                      </Flex>
                    )}
                </Flex>
              </Box>

              {hasHero &&
                heroImage.source === "pexels" &&
                heroImage.attribution?.photographer && (
                  <Text
                    position="absolute"
                    bottom={2}
                    right={3}
                    fontSize="xs"
                    color="whiteAlpha.800"
                    zIndex={1}
                  >
                    Photo by{" "}
                    <Link
                      href={heroImage.attribution.photographerUrl}
                      isExternal
                      color="whiteAlpha.900"
                      textDecoration="underline"
                    >
                      {heroImage.attribution.photographer}
                    </Link>{" "}
                    on{" "}
                    <Link
                      href={heroImage.attribution.pexelsUrl}
                      isExternal
                      color="whiteAlpha.900"
                      textDecoration="underline"
                    >
                      Pexels
                    </Link>
                  </Text>
                )}
            </Box>
          );
        })()}

        <Box width="100%" mb={12}>
          <Divider borderColor="gray.300" mb={8} />

          {/* Available Savings Header */}
          <Heading
            as="h2"
            size="md"
            mb={6}
            textAlign={{ base: "center", md: "left" }}
          >
            {translate("businessList.availableSavings")}
          </Heading>

          {/* Filter / Search */}
          <Flex
            direction={{ base: "column", sm: "row" }}
            gap={3}
            mb={6}
            width="100%"
          >
            <InputGroup flex={1}>
              <InputLeftElement pointerEvents="none" color="gray.400">
                <FaSearch />
              </InputLeftElement>
              <Input
                placeholder={translate("businessList.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                bg="white"
                focusBorderColor="black"
              />
              {searchTerm && (
                <InputRightElement>
                  <IconButton
                    icon={<FaTimes />}
                    size="xs"
                    variant="ghost"
                    aria-label="Clear search"
                    onClick={() => setSearchTerm("")}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            {categories.length > 1 && (
              <Select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                maxW={{ base: "100%", sm: "240px" }}
                bg="white"
                focusBorderColor="black"
              >
                <option value="all">
                  {translate("businessList.allCategories")}
                </option>
                {categories.map(({ key, label }) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </Select>
            )}
          </Flex>

          {/* Business Cards Grid — explicit tracks of up to 360px, aligned
              to the start so leftover horizontal space falls to the right
              of the row instead of being distributed between columns. The
              minmax(0, 360px) lets columns shrink if the container is too
              narrow to fit N full-width cards (e.g. tablet portrait). */}
          {filteredBusinesses.length === 0 ? (
            <Box textAlign="center" py={12}>
              <Text color="gray.500">
                {translate("businessList.noResults")}
              </Text>
            </Box>
          ) : (
            <Box
              display="grid"
              gridTemplateColumns={{
                base: "minmax(0, 360px)",
                sm: "repeat(2, minmax(0, 360px))",
                xl: "repeat(3, minmax(0, 360px))",
              }}
              justifyContent={{ base: "center", sm: "start" }}
              gap={{ base: 6, md: 8, lg: 10 }}
              width="100%"
            >
              {filteredBusinesses.map((business) => (
                <BusinessCard
                  key={business.id}
                  business={{
                    id: business.id,
                    name: getLocalizedText(business.name),
                    category: getLocalizedText(business.category),
                    categoryKey: business.category,
                    description: getLocalizedText(business.description),
                    imageUrl: business.logoUrl,
                    websiteUrl: business.websiteUrl,
                    googleProfileUrl: business.googleProfileUrl,
                    coupon: {
                      title: getLocalizedText(business.offerTitle),
                      description: getLocalizedText(business.offerDescription),
                      termsAndExclusions: getLocalizedText(
                        business.termsAndExclusions
                      ),
                      maxValue: business.maxValue,
                    },
                  }}
                  translations={{
                    language,
                    couponDetails: translate("businessList.couponDetails"),
                    visitWebsite: translate("businessList.website"),
                    googleProfile: translate("businessList.googleProfile"),
                    termsAndExclusions: translate(
                      "businessList.termsAndExclusions"
                    ),
                    maxValue: translate("businessList.maxValue"),
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Purchase Form Modal */}
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent p={2}>
            <ModalHeader>{translate("addCustomer.heading")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <PurchaseForm
                campaignId={campaignId}
                campaign={campaign}
                onClose={onClose}
                agentId={agentId}
              />
            </ModalBody>
          </ModalContent>
        </Modal>
      </Container>
    </Box>
  );
};

export default PurchasePage;
